"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { sendGTMEvent } from "@next/third-parties/google";
import { Button, Dialog, DialogTrigger, Heading, Modal, ModalOverlay } from "react-aria-components";
import { useId } from "react";
import { X } from "lucide-react";
import type { SegmentBadge } from "@/types/segments";
import MarkdownContent from "@/components/content/MarkdownContent";

interface CollectionSealProps {
    badge: SegmentBadge;
    placement: "card" | "hero";
    vehicleVin?: string;
}

const fallbackDisclaimer = (badge: SegmentBadge) =>
    `This vehicle is included in the ${badge.segmentName} collection based on the collection’s current criteria. ` +
    "Collection eligibility and vehicle availability can change as inventory is updated.";

export default function CollectionSeal({ badge, placement, vehicleVin }: CollectionSealProps) {
    const descriptionId = useId();
    const pathname = usePathname();
    const isCard = placement === "card";
    const collectionPath = `/cars/collections/${badge.slug}`;
    const isCollectionPage = pathname.replace(/\/$/, "") === collectionPath;
    const disclaimer = badge.disclaimer || fallbackDisclaimer(badge);

    const handleOpen = () => {
        sendGTMEvent({
            event: "collection_badge_open",
            collection_slug: badge.slug,
            collection_name: badge.segmentName,
            badge_placement: placement,
            ...(vehicleVin ? { vehicle_vin: vehicleVin } : {})
        });
    };

    return (
        <DialogTrigger>
            <Button
                onPress={handleOpen}
                aria-label={`${badge.label}: learn about this collection designation`}
                className={`t-group t-shrink-0 t-rounded-full t-border t-border-slate-200/90 t-bg-white t-p-1 t-shadow-[0_4px_0_rgba(28,29,31,0.16),0_10px_20px_rgba(28,29,31,0.14)] t-transition-[transform,box-shadow,border-color] t-duration-200 hover:-t-translate-y-0.5 hover:t-border-automotive-primary/30 hover:t-shadow-[0_6px_0_rgba(28,29,31,0.14),0_14px_24px_rgba(28,29,31,0.18)] active:t-translate-y-px active:t-shadow-[0_2px_0_rgba(28,29,31,0.16),0_6px_12px_rgba(28,29,31,0.14)] focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 ${
                    isCard
                        ? "t-flex t-h-16 t-w-16 t-items-center t-justify-center"
                        : "t-inline-flex t-h-32 t-w-32 t-items-center t-justify-center sm:t-h-[191px] sm:t-w-[191px]"
                }`}>
                {badge.imageUrl ? (
                    <span
                        className={`t-block t-shrink-0 t-overflow-hidden t-rounded-full t-bg-white t-transition-transform t-duration-200 group-hover:t-scale-[1.02] ${
                            isCard ? "t-h-14 t-w-14" : "t-h-[120px] t-w-[120px] sm:t-h-[181px] sm:t-w-[181px]"
                        }`}>
                        <Image
                            src={badge.imageUrl}
                            alt={badge.imageAlt || `${badge.label} collection badge`}
                            width={isCard ? 56 : 181}
                            height={isCard ? 56 : 181}
                            sizes={isCard ? "56px" : "(min-width: 640px) 181px, 120px"}
                            quality={75}
                            className={`t-object-contain ${
                                isCard ? "t-h-14 t-w-14" : "t-h-[120px] t-w-[120px] sm:t-h-[181px] sm:t-w-[181px]"
                            }`}
                        />
                    </span>
                ) : (
                    <span
                        className={`t-inline-flex t-items-center t-justify-center t-rounded-full t-font-bold t-uppercase t-tracking-wider t-shadow-sm ${
                            isCard ? "t-min-h-12 t-w-full t-px-2 t-py-1 t-text-[10px]" : "t-h-full t-w-full t-p-4 t-text-base sm:t-text-xl"
                        }`}
                        style={{ backgroundColor: badge.backgroundColor, color: badge.textColor }}>
                        {badge.label}
                    </span>
                )}
            </Button>

            <ModalOverlay
                isDismissable
                className="t-fixed t-inset-0 t-z-[100] t-flex t-items-end t-justify-center t-bg-black/55 t-p-0 t-backdrop-blur-sm t-transition-opacity entering:t-opacity-100 exiting:t-opacity-0 sm:t-items-center sm:t-p-6">
                <Modal className="t-max-h-[88dvh] t-w-full t-overflow-hidden t-rounded-t-3xl t-bg-white t-shadow-2xl t-outline-none t-transition-transform entering:t-translate-y-0 exiting:t-translate-y-full sm:t-max-w-lg sm:t-rounded-3xl sm:entering:t-scale-100 sm:exiting:t-scale-95">
                    <Dialog
                        aria-describedby={descriptionId}
                        className="t-flex t-max-h-[88dvh] t-flex-col focus:t-outline-none">
                        {({ close }) => (
                            <>
                                <div className="t-relative t-flex t-shrink-0 t-items-center t-gap-4 t-bg-[#1C1D1F] t-px-5 t-py-5 t-text-white sm:t-px-6">
                                    <span className="t-relative t-h-20 t-w-20 t-shrink-0 t-overflow-hidden t-rounded-full t-bg-white t-shadow-lg">
                                        {badge.imageUrl ? (
                                            <Image
                                                src={badge.imageUrl}
                                                alt=""
                                                fill
                                                sizes="80px"
                                                quality={75}
                                                className="t-object-contain"
                                            />
                                        ) : (
                                            <span
                                                className="t-flex t-h-full t-w-full t-items-center t-justify-center t-p-2 t-text-center t-text-xs t-font-bold t-uppercase"
                                                style={{ backgroundColor: badge.backgroundColor, color: badge.textColor }}>
                                                {badge.label}
                                            </span>
                                        )}
                                    </span>
                                    <div className="t-min-w-0">
                                        <Heading slot="title" className="t-m-0 t-text-xl t-font-bold t-leading-tight sm:t-text-2xl">
                                            {badge.modalTitle || `About ${badge.segmentName}`}
                                        </Heading>
                                    </div>
                                    <Button
                                        onPress={close}
                                        aria-label="Close collection information"
                                        className="t-absolute t-right-3 t-top-3 t-flex t-h-10 t-w-10 t-items-center t-justify-center t-rounded-full t-bg-white/10 t-text-white hover:t-bg-white/20 focus:t-outline-none focus:t-ring-2 focus:t-ring-[#E7FF34]">
                                        <X className="t-h-5 t-w-5" aria-hidden="true" />
                                    </Button>
                                </div>

                                <div className="t-overflow-y-auto t-px-5 t-py-5 sm:t-px-6 sm:t-py-6">
                                    <MarkdownContent
                                        id={descriptionId}
                                        text={disclaimer}
                                        className="t-text-sm t-leading-6 t-text-ecommerce-text sm:t-text-base sm:t-leading-7"
                                    />
                                    {!isCollectionPage && (
                                        <div className="t-mt-6 t-flex t-justify-end">
                                            <Link
                                                href={collectionPath}
                                                className="t-inline-flex t-min-h-11 t-items-center t-justify-center t-rounded-full t-bg-[#1C1D1F] t-px-5 t-py-2.5 t-text-sm t-font-semibold t-text-white t-no-underline hover:t-bg-[#4C5054] focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2">
                                                Explore All {badge.segmentName}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </DialogTrigger>
    );
}
